import { useMemo } from 'react';

import useTranslations from '@/i18n/useTranslations';

const formatTimestamp = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  try {
    const date = new Date(timestamp);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return '';
  }
};

export default function ChromaticMusicGallery({ pieces, toastMessage, onDownload }) {
  const t = useTranslations('common.newspaper.art_games.chromatic_music.gallery');
  const gallery = useMemo(() => (Array.isArray(pieces) ? pieces : []), [pieces]);

  return (
    <div className="chromatic-music__panel chromatic-music__panel--gallery" aria-label={t('aria.saved_gallery')}>
      <div className="chromatic-music__save-header">
        <h2>{t('title')}</h2>
        {toastMessage && (
          <span className="chromatic-music__save-toast" role="status" aria-live="polite">
            {toastMessage}
          </span>
        )}
      </div>

      {gallery.length === 0 ? (
        <p className="chromatic-music__gallery-empty">{t('empty')}</p>
      ) : (
        <ul className="chromatic-music__gallery" role="list">
          {gallery.map((piece) => {
            const formatted = formatTimestamp(piece.timestamp);
            const tempoLabel = typeof piece.tempo === 'number' ? t('tempo', { tempo: piece.tempo }) : null;
            const layerLabel =
              typeof piece.layerCount === 'number'
                ? t('layers', { count: piece.layerCount })
                : null;

            return (
              <li key={piece.id} className="chromatic-music__gallery-item">
                <figure className="chromatic-music__gallery-figure">
                  <div className="chromatic-music__gallery-preview">
                    <img src={piece.imageUrl} alt={t('saved_alt')} loading="lazy" />
                  </div>
                  <figcaption className="chromatic-music__gallery-meta">
                    {formatted && <span>{formatted}</span>}
                    <div className="chromatic-music__gallery-tags">
                      {tempoLabel && <span className="chromatic-music__gallery-tag">{tempoLabel}</span>}
                      {layerLabel && <span className="chromatic-music__gallery-tag">{layerLabel}</span>}
                    </div>
                  </figcaption>
                </figure>

                {piece.audioUrl ? (
                  <audio
                    className="chromatic-music__gallery-audio"
                    controls
                    preload="none"
                    src={piece.audioUrl}
                  >
                    {t('audio_not_supported')}
                  </audio>
                ) : (
                  <p className="chromatic-music__gallery-audio--empty">{t('audio_unavailable')}</p>
                )}

                <button
                  type="button"
                  className="chromatic-music__gallery-download"
                  onClick={() => onDownload?.(piece)}
                >
                  {t('download_png')}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
